/**
 * @voltxampere/presets — Arduino button input topology (Sprint 50-6)
 *
 * Push-button on a digital input pin with internal/external pull-up
 * resistor. When button open, the pull-up holds the input HIGH (5V);
 * pressing the button shorts the input to ground (0V). Standard
 * Arduino "Button" tutorial wiring.
 *
 * Modeled with the button OPEN (HIGH state) — the Sprint 22 switch
 * canonical type would let us toggle, but this preset locks the
 * idle state for DC verification.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const arduinoButtonInput = {
    id: "arduino-button-input",
    name: { tr: "Arduino Buton Girişi Topolojisi", en: "Arduino Button Input Topology" },
    category: "mcu",
    difficulty: 1,
    description: {
        tr: "Pull-up dirençli buton girişi. Buton açık iken giriş HIGH (5V), basılınca LOW.",
        en: "Pull-up resistor button input. Open button = HIGH (5V); pressed = LOW.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Arduino Button Input",
        description: "Pull-up button on Arduino digital input",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "btn_in" }),
        ],
        components: [
            makeComponent({
                id: "V_CC",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V_CC",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R_PULLUP",
                type: "resistor",
                params: { resistance: 10000 },
                terminals: { p1: "net_vcc", p2: "net_btn_in" },
                label: "R_PULLUP",
                position: { x: 0, y: -60 },
                rotation: 90,
            }),
            // High-impedance representation of MCU input pin (1MΩ to GND).
            makeComponent({
                id: "R_INPUT",
                type: "resistor",
                params: { resistance: 1e6 },
                terminals: { p1: "net_btn_in", p2: "net_gnd" },
                label: "R_INPUT (MCU)",
                position: { x: 80, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [],
    }),
    expectedValues: [
        {
            node: "btn_in",
            analysis: "dc",
            value: 5,
            tolerance: 0.05,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Pull-up direnci giriş pinini default HIGH'e çeker.",
            en: "Pull-up resistor pulls the input pin HIGH by default.",
        },
        {
            tr: "MCU input pini yüksek empedansta (~∞), gerilim bölücü etkisi yoktur.",
            en: "MCU input pins are high-impedance — no significant divider effect.",
        },
    ],
    references: [
        "Arduino Button Tutorial (https://docs.arduino.cc/built-in-examples/digital/Button/)",
    ],
};
//# sourceMappingURL=arduino-button-input.js.map